/**
 * Compliance Report Page
 *
 * Displays the compliance analysis results for a scraped website.
 * Accessible at: /console/$orgId/tools/website-checker/report/$id
 */

import { PageController } from '@client/components/_core/layout/PageController';
import type { ShareableRouteContext } from '@client/types/router';
import { createFileRoute } from '@tanstack/react-router';
import { ReportContent } from './ReportContent';

export const Route = createFileRoute('/console/$orgId/tools/website-checker/report/$id')({
    params: {
        parse({ id }) {
            return { id: BigInt(id) };
        },
    },
    beforeLoad({ context: { trpcUtils }, params: { id } }): ShareableRouteContext {
        // Dynamic breadcrumb showing website domain
        async function breadcrumb() {
            const report = await trpcUtils.websiteChecker.getReport.ensureData({
                scrapedDataId: id,
            });
            if (!report) return 'Compliance Report';
            // Extract domain from URL for cleaner display
            const domain = new URL(report.websiteUrl).hostname;
            return `${domain} report`;
        }

        async function title() {
            const report = await trpcUtils.websiteChecker.getReport.ensureData({
                scrapedDataId: id,
            });
            if (!report) return 'Compliance Report';
            return `Compliance Report - ${report.websiteUrl}`;
        }

        return {
            breadcrumb: { title: breadcrumb },
            ui: { title },
        };
    },
    staticData: {
        featureFlag: 'compliance-website-checker',
        ui: {
            widthVariant: 'default',
        },
    },
    component: ComplianceReportPage,
});

function ComplianceReportPage() {
    const { id } = Route.useParams();

    return (
        <PageController route={Route}>
            <ReportContent scrapedDataId={id} />
        </PageController>
    );
}
